import type { User } from "../services/UserService";

export const DEAL_INTENTS = ["online", "instore"] as const;
export type DealIntent = (typeof DEAL_INTENTS)[number];

// Free accounts live on the public deals site (see AppRouter), so their online feed is the
// home feed. The members-app welcome sheet (?welcome=free on /walmart/products) is gone for them.
export const ONLINE_FEED_PATH = "/";
export const FREE_WELCOME_PATH = ONLINE_FEED_PATH;
export const DEAL_ONBOARDING_PATH = "/onboarding";

type OnboardingUser = Partial<Pick<
  User,
  "verified" | "admin" | "retailers" | "dealIntent" | "setUp" | "tourSeen" | "zipCode" | "created"
>> | null;

/** Users created before this keep the old feed entry and skip the new chooser. */
export const DEAL_ONBOARDING_LAUNCH_MS = Date.parse("2026-08-17T00:00:00Z");

export function isDealIntent(value?: string | null): value is DealIntent {
  return value === "online" || value === "instore";
}

export function hasSavedZip(zip?: string | null): boolean {
  return !!zip && /^\d{5}$/.test(zip);
}

/**
 * Mirrors pinto's deal-mode gate: a new verified account must choose between
 * online-only (free) and local stores (paid) before it sees the app. Pinto
 * records the choice as `setUp`; web also records `dealIntent`, and either
 * flag counts so a user who onboarded on one client is not asked again on the
 * other.
 */
export function needsDealOnboarding(user: OnboardingUser): boolean {
  if (!user?.verified || user.admin) return false;
  if (user.retailers?.length) return false;
  if (user.setUp || isDealIntent(user.dealIntent)) return false;
  if (user.tourSeen || user.zipCode) return false;
  if (!user.created || user.created < DEAL_ONBOARDING_LAUNCH_MS) return false;
  return true;
}

/** Hold the guest return path until onboarding finishes (or email is verified). */
export function heldPostAuthPath(user: OnboardingUser): string | null {
  if (!user?.verified) return "/verify";
  if (needsDealOnboarding(user)) return DEAL_ONBOARDING_PATH;
  return null;
}

export function defaultPostAuthPath(user: OnboardingUser): string {
  return user?.retailers?.length ? "/" : FREE_WELCOME_PATH;
}

export function isDealOnboardingPath(pathname: string): boolean {
  return pathname === DEAL_ONBOARDING_PATH || pathname.startsWith(`${DEAL_ONBOARDING_PATH}/`);
}

export function isDealOnboardingAllowedPath(pathname: string): boolean {
  return (
    isDealOnboardingPath(pathname) ||
    pathname === "/setup" ||
    pathname === "/success" ||
    pathname === "/contact-us" ||
    pathname === "/verify"
  );
}

export function destinationAfterDealIntent(intent: DealIntent): string {
  return intent === "instore" ? "/setup" : ONLINE_FEED_PATH;
}
